import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { LuArrowLeft, LuPenSquare, LuShoppingCart } from "react-icons/lu";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import DetailAccountInfo from "../../components/materialLines/DetailAccountInfo";
import DetailHeader from "../../components/materialLines/DetailHeader";
import DetailPartyInfo from "../../components/materialLines/DetailPartyInfo";
import DetailSection from "../../components/materialLines/DetailSection";
import MaterialPhoto from "../../components/materialLines/MaterialPhoto";
import PurchaseStatusBadge from "../../components/materialLines/PurchaseStatusBadge";
import Button from "../../components/ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import {
  detailCardClass,
  detailIndexHeadClass,
  detailInfoLabelClass,
  detailNumberCellClass,
  detailNumberHeadClass,
  detailNumberTotalClass,
  detailPhotoHeadClass,
  detailTableHeadRowClass,
  detailTableWrapClass,
  mobileCardClass,
  moneyCellClass,
  pageTitleClass,
  tableHeadCellClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import {
  clearCurrentMaterialPurchase,
  fetchMaterialPurchaseDetail,
  getCurrentMaterialPurchase,
  getCurrentMaterialPurchaseLoading,
} from "../../features/materialPurchases/materialPurchasesSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { makeReport } from "../../services/download.service";
import {
  formatAmount,
  formatGujaratiLongDateTime,
  formatQty,
} from "../../static/materialLine";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { BreadcrumbType } from "../../types/common";
import { MaterialPurchaseItemProps } from "../../types/materialPurchase";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "મટીરીયલ ખરીદી", href: route.materialPurchases },
  { label: "મટીરીયલ ખરીદી વિગતો" },
];

const orderLinkClass = "font-medium text-[#113F67] hover:underline";

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const totalCellClass = "font-bold text-[#113F67]";

// The rows under the lines with the lines' total and the additional costs' total, as the reference app's dies purchase
// page has them.
const subtotalRowClass = "bg-gray-100 hover:bg-gray-100";

const subtotalLabelClass = "text-right font-semibold text-[#113F67]";

const orderPath = (id: number) =>
  route.materialOrderDetail.replace(":id", id as unknown as string);

const MaterialPurchaseDetailPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const purchase = useAppSelector(getCurrentMaterialPurchase);
  const isLoading = useAppSelector(getCurrentMaterialPurchaseLoading);
  const loggingUser = useAppSelector(getLoggingUser);

  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.materialPurchasesUpdate
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.materialPurchasesReport
  );
  // Rates, amounts and the status are only for a user who may see rates.
  const canRate = hasPermission(
    loggingUser?.permissions,
    permission.materialPurchasesRate
  );
  const canReadOrders = hasPermission(
    loggingUser?.permissions,
    permission.materialOrdersRead
  );

  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchMaterialPurchaseDetail(Number(id)));
    }
    return () => {
      dispatch(clearCurrentMaterialPurchase());
    };
  }, [id, dispatch]);

  const items = purchase?.items || [];
  const totalQty = items.reduce((sum, item) => sum + (Number(item.qty) || 0), 0);

  // Amounts: only a user who may see rates gets them.
  const costs = canRate ? purchase?.additional_costs || [] : [];

  // The orders the purchase received lines of.
  const orderIds = Array.from(
    new Set(
      items
        .map((item) => item.order_item?.order_id || 0)
        .filter((orderId) => orderId > 0)
    )
  ).sort((a, b) => a - b);

  // Back to where the user came from, or to the purchase list when the page was opened directly.
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(route.materialPurchases);
    }
  };

  const handleReportExport = async () => {
    setIsReportLoading(true);
    const file = await makeReport(`/v1/admin/material-purchases/${id}/pdf`);
    setIsReportLoading(false);

    if (file) dispatch(updatePdfFile(file));
  };

  const orderLink = (orderId: number) =>
    canReadOrders ? (
      <Link key={orderId} to={orderPath(orderId)} className={orderLinkClass}>
        #{orderId}
      </Link>
    ) : (
      <span key={orderId} className="font-medium text-[#113F67]">
        #{orderId}
      </span>
    );

  const photo = (item: MaterialPurchaseItemProps) => (
    <MaterialPhoto
      name={item.material?.name || "-"}
      imageUrl={item.material?.image_url}
      className="h-10 w-10 shrink-0 rounded-md"
      compact
    />
  );

  // A line saved without a rate waits for one.
  const rateText = (item: MaterialPurchaseItemProps) =>
    item.rate_pending ? (
      <span className="font-semibold text-orange-600">રેટ બાકી</span>
    ) : (
      formatAmount(item.price)
    );

  const amountText = (item: MaterialPurchaseItemProps) =>
    item.rate_pending ? "-" : formatAmount(item.amount);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>મટીરીયલ ખરીદી વિગતો</h1>
      </div>

      {isLoading ? (
        <div className={detailCardClass}>
          <LoadingState />
        </div>
      ) : !purchase ? (
        <div className={cx(detailCardClass, "text-center text-gray-500")}>
          મટીરીયલ ખરીદી ની વિગતો મળી નથી
        </div>
      ) : (
        <div className={detailCardClass}>
          <DetailHeader icon={LuShoppingCart} title="મટીરીયલ ખરીદી" id={purchase.id}>
            <Button variant="outline" className="max-sm:flex-1" onClick={handleBack}>
              <LuArrowLeft className="h-4 w-4" />
              પાછા જાઓ
            </Button>

            {canReport && (
              <Button
                variant="primary"
                className="max-sm:flex-1"
                disabled={isReportLoading}
                onClick={handleReportExport}
              >
                {isReportLoading ? "રિપોર્ટ બની રહ્યો છે..." : "રિપોર્ટ બનાવો"}
              </Button>
            )}

            {canUpdate && (
              <Button
                variant="primary"
                className="max-sm:flex-1"
                onClick={() =>
                  navigate(
                    route.materialPurchaseEdit.replace(
                      ":id",
                      purchase.id as unknown as string
                    )
                  )
                }
              >
                <LuPenSquare className="h-4 w-4" />
                એડિટ કરો
              </Button>
            )}
          </DetailHeader>

          <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-2">
              <span className={detailInfoLabelClass}>તારીખ:</span>
              <span>
                {formatGujaratiLongDateTime(purchase.date)}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-2">
              <span className={detailInfoLabelClass}>ઓર્ડર:</span>
              {orderIds.length > 0 ? (
                <span className="flex flex-wrap items-center gap-x-3">
                  {orderIds.map(orderLink)}
                </span>
              ) : (
                <span className="text-gray-500">ઓર્ડર વગર ખરીદી</span>
              )}
            </div>

            {canRate && (
              <div className="flex items-center gap-2">
                <span className={detailInfoLabelClass}>સ્થિતિ:</span>
                <PurchaseStatusBadge status={purchase.status ?? 1} />
              </div>
            )}
          </div>

          <DetailPartyInfo partyId={purchase.party_id} party={purchase.party} />

          <DetailSection title="મટીરીયલ ખરીદી આઇટમ્સ ની માહિતી">
            <div className={cx(detailTableWrapClass, "max-sm:hidden")}>
              <Table>
                <TableHeader>
                  <TableRow className={detailTableHeadRowClass}>
                    <TableHead className={detailIndexHeadClass}>ક્રમ</TableHead>
                    <TableHead className={detailPhotoHeadClass}>ફોટો</TableHead>
                    <TableHead className={tableHeadCellClass}>મટીરીયલ</TableHead>
                    <TableHead className={detailNumberHeadClass}>જથ્થો</TableHead>
                    {canRate && (
                      <>
                        <TableHead className={detailNumberHeadClass}>ભાવ</TableHead>
                        <TableHead className={detailNumberHeadClass}>રકમ</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell>{photo(item)}</TableCell>
                      <TableCell className="break-words font-semibold text-[#113F67]">
                        {item.material?.name || "-"}
                      </TableCell>
                      <TableCell className={detailNumberCellClass}>
                        {formatQty(item.qty)}
                      </TableCell>
                      {canRate && (
                        <>
                          <TableCell className={detailNumberCellClass}>
                            {rateText(item)}
                          </TableCell>
                          <TableCell className={detailNumberCellClass}>
                            {amountText(item)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}

                  {/* As the reference app's dies purchase page: the lines' total, then the additional costs with
                      theirs; the footer has the purchase's total of both. */}
                  {canRate && (
                    <TableRow className={subtotalRowClass}>
                      <TableCell colSpan={3} className={subtotalLabelClass}>
                        આઈટમ કુલ:
                      </TableCell>
                      <TableCell className={detailNumberCellClass}>
                        {formatQty(totalQty)}
                      </TableCell>
                      <TableCell />
                      <TableCell className={detailNumberCellClass}>
                        {formatAmount(purchase.items_total)}
                      </TableCell>
                    </TableRow>
                  )}

                  {costs.length > 0 && (
                    <>
                      <TableRow className="bg-[#113F67]/5 hover:bg-[#113F67]/5">
                        <TableCell
                          colSpan={6}
                          className="text-base font-semibold text-[#113F67]"
                        >
                          વધારાના ખર્ચ
                        </TableCell>
                      </TableRow>

                      {costs.map((cost, index) => (
                        <TableRow key={`${cost.note}-${index}`}>
                          <TableCell className="font-medium">{index + 1}</TableCell>
                          <TableCell
                            colSpan={4}
                            className="break-words font-semibold text-[#113F67]"
                          >
                            {cost.note}
                          </TableCell>
                          <TableCell className={detailNumberCellClass}>
                            {formatAmount(cost.amount)}
                          </TableCell>
                        </TableRow>
                      ))}

                      <TableRow className={subtotalRowClass}>
                        <TableCell colSpan={5} className={subtotalLabelClass}>
                          વધારાના ખર્ચ કુલ:
                        </TableCell>
                        <TableCell className={detailNumberCellClass}>
                          {formatAmount(purchase.additional_total)}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>

                <TableFooter>
                  <TableRow className="bg-[#113F67]/10 hover:bg-[#113F67]/10">
                    {canRate ? (
                      <>
                        <TableCell colSpan={5} className={cx(totalCellClass, "text-right")}>
                          કુલ રકમ:
                        </TableCell>
                        <TableCell className={detailNumberTotalClass}>
                          {formatAmount(purchase.total)}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell colSpan={3} className={cx(totalCellClass, "text-right")}>
                          કુલ:
                        </TableCell>
                        <TableCell className={detailNumberTotalClass}>
                          {formatQty(totalQty)}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableFooter>
              </Table>
            </div>

            <div className="space-y-4 sm:hidden">
              {items.map((item, index) => (
                <div key={item.id} className={mobileCardClass}>
                  <div className="flex items-center gap-3 border-b p-4">
                    {photo(item)}
                    <span className="break-words font-semibold text-[#113F67]">
                      {item.material?.name || "-"}
                    </span>
                  </div>

                  <div className="space-y-3 p-4 text-sm">
                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>ક્રમ</span>
                      <span className="font-medium">{index + 1}</span>
                    </div>

                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>જથ્થો</span>
                      <span className={moneyCellClass}>{formatQty(item.qty)}</span>
                    </div>

                    {canRate && (
                      <>
                        <div className={cardRowClass}>
                          <span className={cardLabelClass}>ભાવ</span>
                          <span className={moneyCellClass}>{rateText(item)}</span>
                        </div>

                        <div className={cardRowClass}>
                          <span className={cardLabelClass}>રકમ</span>
                          <span className={moneyCellClass}>{amountText(item)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}

              <div className={mobileCardClass}>
                {costs.length > 0 && (
                  <div className="space-y-3 border-b p-4 text-sm">
                    <div className={cardLabelClass}>વધારાના ખર્ચ</div>

                    {costs.map((cost, index) => (
                      <div key={`${cost.note}-${index}`} className={cardRowClass}>
                        <span className="break-words font-semibold text-[#113F67]">
                          {cost.note}
                        </span>
                        <span className={moneyCellClass}>
                          {formatAmount(cost.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-3 p-4 text-sm">
                  <div className={cardRowClass}>
                    <span className={canRate ? cardLabelClass : totalCellClass}>
                      કુલ જથ્થો
                    </span>
                    <span
                      className={
                        canRate ? moneyCellClass : cx(totalCellClass, "text-right")
                      }
                    >
                      {formatQty(totalQty)}
                    </span>
                  </div>

                  {canRate && (
                    <>
                      <div className={cardRowClass}>
                        <span className={cardLabelClass}>આઈટમ કુલ</span>
                        <span className={moneyCellClass}>
                          {formatAmount(purchase.items_total)}
                        </span>
                      </div>

                      {costs.length > 0 && (
                        <div className={cardRowClass}>
                          <span className={cardLabelClass}>વધારાના ખર્ચ કુલ</span>
                          <span className={moneyCellClass}>
                            {formatAmount(purchase.additional_total)}
                          </span>
                        </div>
                      )}

                      <div className={cx(cardRowClass, "border-t pt-3")}>
                        <span className={totalCellClass}>કુલ રકમ</span>
                        <span className={cx(totalCellClass, "text-right")}>
                          {formatAmount(purchase.total)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </DetailSection>

          <DetailAccountInfo
            creator={purchase.creator}
            updater={purchase.updater}
            createdAt={purchase.created_at}
            updatedAt={purchase.updated_at}
          />
        </div>
      )}
    </AppLayout>
  );
};
export default MaterialPurchaseDetailPage;
